/**
 * Anomaly Consolidation Service
 * 
 * Consolidates multiple anomalies of the same type into summarized messages
 * to reduce redundancy and improve clarity in the detected issues pane.
 * 
 * This service uses the same logic as the policy violation consolidation
 * to provide a consistent user experience across the application.
 */

import type { Anomaly, AnomalyType, AnomalySeverity } from '@/types/anomaly';

export interface ConsolidatedAnomaly {
  id: string;
  type: AnomalyType;
  severity: AnomalySeverity;
  count: number;
  summary: string;
  details: Array<{
    day?: string;
    date?: string;
    message: string;
    value?: number;
    threshold?: number;
  }>;
  insight: string;
  originalAnomalies: Anomaly[];
  anomalies?: Anomaly[]; // Alias for originalAnomalies for easier access
}

/**
 * Determine if anomalies should be consolidated
 * Returns true if there are 2+ anomalies of the same type
 */
export function shouldConsolidate(anomalies: Anomaly[]): boolean {
  if (anomalies.length < 2) return false;
  
  const typeCounts = new Map<AnomalyType, number>();
  anomalies.forEach(anomaly => {
    typeCounts.set(anomaly.type, (typeCounts.get(anomaly.type) || 0) + 1);
  });
  
  // Check if any type has 2+ occurrences
  return Array.from(typeCounts.values()).some(count => count >= 2);
}

/**
 * Group anomalies by type
 */
function groupAnomaliesByType(anomalies: Anomaly[]): Map<AnomalyType, Anomaly[]> {
  const grouped = new Map<AnomalyType, Anomaly[]>();
  
  anomalies.forEach(anomaly => {
    const existing = grouped.get(anomaly.type) || [];
    existing.push(anomaly);
    grouped.set(anomaly.type, existing);
  });
  
  return grouped;
}

/**
 * Get the highest severity from a list of anomalies
 */
function getHighestSeverity(anomalies: Anomaly[]): AnomalySeverity {
  if (anomalies.some(a => a.severity === 'high')) return 'high';
  if (anomalies.some(a => a.severity === 'medium')) return 'medium';
  return 'low';
}

/**
 * Get day name from day string (e.g., 'monday' -> 'Monday')
 */
function formatDayName(day?: string): string {
  if (!day) return '';
  return day.charAt(0).toUpperCase() + day.slice(1);
}

/**
 * Format anomaly type as human-readable title
 */
function formatAnomalyType(type: AnomalyType): string {
  const typeMap: Record<AnomalyType, string> = {
    'daily-hours-exceeded': 'Daily Hours Exceeded',
    'weekly-hours-exceeded': 'Weekly Hours Exceeded',
    'weekend-work-no-approval': 'Weekend Work Without Approval',
    'missing-description': 'Missing Descriptions',
    'missing-activity-type': 'Missing Activity Types',
    'hours-above-average': 'Hours Above Average',
    'unusual-project-allocation': 'Unusual Project Allocations',
    'late-submission': 'Late Submission',
    'first-project-entry': 'First Project Entry',
    'scheduled-booking-variance': 'Scheduled Booking Variances',
    'scheduled-booking-dismissed': 'Dismissed Scheduled Bookings',
    'time-and-absence-conflict': 'Time and Absence Conflicts',
  };
  
  return typeMap[type] || type;
}

/**
 * Generate insight message based on anomaly type and count
 */
function generateInsight(type: AnomalyType, count: number, severity: AnomalySeverity): string {
  if (count === 1) {
    // Single anomaly - provide specific guidance
    switch (type) {
      case 'daily-hours-exceeded':
        return 'Ensure overtime is approved and necessary.';
      case 'weekly-hours-exceeded':
        return 'Ensure overtime is approved and necessary.';
      case 'weekend-work-no-approval':
        return 'Weekend work requires prior approval. Please verify authorization.';
      case 'missing-description':
        return 'Add descriptions to help managers understand the work performed.';
      case 'missing-activity-type':
        return 'Specify activity types for accurate time tracking.';
      case 'hours-above-average':
        return 'Verify that the increased hours are accurate and necessary.';
      case 'unusual-project-allocation':
        return 'Confirm that project allocation is correct.';
      case 'late-submission':
        return 'Submit timesheets on time to ensure timely processing.';
      case 'first-project-entry':
        return 'First time working on this project. Ensure project code is correct.';
      case 'scheduled-booking-variance':
        return 'Review variance and provide explanation if needed.';
      case 'scheduled-booking-dismissed':
        return 'Dismissed booking has been noted.';
      case 'time-and-absence-conflict':
        return 'Verify whether the time entry or absence record is correct. Typically, only one should be recorded.';
      default:
        return 'Please review and address this issue.';
    }
  } else {
    // Multiple anomalies - provide general guidance
    switch (type) {
      case 'daily-hours-exceeded':
        return 'Multiple days exceed daily limits. Ensure all overtime is approved and necessary.';
      case 'weekly-hours-exceeded':
        return 'Weekly hours significantly exceeded. Ensure overtime is approved and necessary.';
      case 'weekend-work-no-approval':
        return 'Multiple weekend days require approval. Please verify authorization for all weekend work.';
      case 'missing-description':
        return 'Multiple entries missing descriptions. Add details to help managers understand the work performed.';
      case 'missing-activity-type':
        return 'Multiple entries missing activity types. Specify types for accurate time tracking.';
      case 'hours-above-average':
        return 'Hours significantly above average on multiple days. Verify accuracy and necessity.';
      case 'unusual-project-allocation':
        return 'Multiple unusual project allocations detected. Confirm all project codes are correct.';
      case 'scheduled-booking-variance':
        return 'Multiple variances detected. Review and provide explanations where needed.';
      case 'scheduled-booking-dismissed':
        return 'Multiple bookings dismissed. All dismissals have been noted.';
      case 'time-and-absence-conflict':
        return 'Multiple days have both time entries and absences. Verify which records are correct for each day.';
      default:
        return 'Multiple issues detected. Please review and address all items.';
    }
  }
}

/**
 * Consolidate daily-type anomalies (anomalies with day information)
 */
function consolidateDailyAnomalies(type: AnomalyType, anomalies: Anomaly[]): ConsolidatedAnomaly {
  // Sort by day order (Monday -> Sunday)
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const sorted = [...anomalies].sort((a, b) => {
    const dayA = a.day?.toLowerCase() || '';
    const dayB = b.day?.toLowerCase() || '';
    return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
  });
  
  const severity = getHighestSeverity(sorted);
  const count = sorted.length;
  
  // Format details
  const details = sorted.map(anomaly => ({
    day: formatDayName(anomaly.day),
    message: anomaly.message,
    value: anomaly.value,
    threshold: anomaly.threshold,
  }));
  
  return {
    id: `consolidated-${type}-${sorted[0].id}`,
    type,
    severity,
    count,
    summary: `${formatAnomalyType(type)} (${count} occurrence${count > 1 ? 's' : ''})`,
    details,
    insight: generateInsight(type, count, severity),
    originalAnomalies: sorted,
    anomalies: sorted, // Alias for easier access
  };
}

/**
 * Consolidate weekly-type anomalies (single occurrence per timesheet)
 */
function consolidateWeeklyAnomalies(type: AnomalyType, anomalies: Anomaly[]): ConsolidatedAnomaly {
  const severity = getHighestSeverity(anomalies);
  const count = anomalies.length;
  
  // For weekly anomalies, typically there's only one
  const details = anomalies.map(anomaly => ({
    message: anomaly.message,
    value: anomaly.value,
    threshold: anomaly.threshold,
  }));
  
  return {
    id: `consolidated-${type}-${anomalies[0].id}`,
    type,
    severity,
    count,
    summary: formatAnomalyType(type),
    details,
    insight: generateInsight(type, count, severity),
    originalAnomalies: anomalies,
    anomalies: anomalies, // Alias for easier access
  };
}

/**
 * Consolidate other types of anomalies
 */
function consolidateOtherAnomalies(type: AnomalyType, anomalies: Anomaly[]): ConsolidatedAnomaly {
  // Sort by date if available, otherwise keep original order
  const sorted = [...anomalies].sort((a, b) => {
    if (a.day && b.day) {
      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      return dayOrder.indexOf(a.day.toLowerCase()) - dayOrder.indexOf(b.day.toLowerCase());
    }
    return 0;
  });
  
  const severity = getHighestSeverity(sorted);
  const count = sorted.length;
  
  // Format details
  const details = sorted.map(anomaly => ({
    day: anomaly.day ? formatDayName(anomaly.day) : undefined,
    message: anomaly.message,
    value: anomaly.value,
    threshold: anomaly.threshold,
  }));
  
  return {
    id: `consolidated-${type}-${sorted[0].id}`,
    type,
    severity,
    count,
    summary: count > 1 
      ? `${formatAnomalyType(type)} (${count} occurrence${count > 1 ? 's' : ''})`
      : formatAnomalyType(type),
    details,
    insight: generateInsight(type, count, severity),
    originalAnomalies: sorted,
    anomalies: sorted, // Alias for easier access
  };
}

/**
 * Main consolidation function
 * Groups anomalies by type and consolidates those with 2+ occurrences
 */
export function consolidateAnomalies(anomalies: Anomaly[]): ConsolidatedAnomaly[] {
  if (anomalies.length === 0) return [];
  
  // Group by type
  const grouped = groupAnomaliesByType(anomalies);
  
  // Consolidate each group
  const consolidated: ConsolidatedAnomaly[] = [];
  
  grouped.forEach((groupAnomalies, type) => {
    // Determine consolidation strategy based on type
    const isDailyType = [
      'daily-hours-exceeded',
      'weekend-work-no-approval',
      'hours-above-average',
    ].includes(type);
    
    const isWeeklyType = [
      'weekly-hours-exceeded',
      'late-submission',
    ].includes(type);
    
    let consolidatedGroup: ConsolidatedAnomaly;
    
    if (isDailyType) {
      consolidatedGroup = consolidateDailyAnomalies(type, groupAnomalies);
    } else if (isWeeklyType) {
      consolidatedGroup = consolidateWeeklyAnomalies(type, groupAnomalies);
    } else {
      consolidatedGroup = consolidateOtherAnomalies(type, groupAnomalies);
    }
    
    consolidated.push(consolidatedGroup);
  });
  
  // Sort by severity (high -> medium -> low), then alphabetically by type
  consolidated.sort((a, b) => {
    const severityOrder = { high: 0, medium: 1, low: 2 };
    const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return a.type.localeCompare(b.type);
  });
  
  return consolidated;
}
